/** Route table. Pages are lazy-loaded so the first paint (often a phone outdoors) stays small. */
import { ShieldAlert } from 'lucide-react';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import type { Role } from '../shared/api.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { PageSkeleton } from './components/Spinner.js';
import { EmptyState } from './components/States.js';
import { hasRole, useMe } from './lib/session.js';
import { HomeRedirect } from './pages/HomeRedirect.js';
import { ViewFrame } from './shell/ViewFrame.js';

const PAGE_MODULES = {
  my: () => import('./pages/my/MyPlanningPage.js'),
  projects: () => import('./pages/projects/ProjectsPage.js'),
  project: () => import('./pages/projects/ProjectDetailPage.js'),
  resources: () => import('./pages/resources/ResourcesPage.js'),
  admin: () => import('./pages/admin/AdminPage.js'),
  settings: () => import('./pages/settings/SettingsPage.js'),
  notFound: () => import('./pages/NotFoundPage.js'),
  gantt: () => import('./views/planning/GanttPage.js'),
  team: () => import('./views/planning/TeamPage.js'),
  usage: () => import('./views/planning/UsagePage.js'),
};
const MyPlanningPage = lazy(PAGE_MODULES.my);
const ProjectsPage = lazy(PAGE_MODULES.projects);
const ProjectDetailPage = lazy(PAGE_MODULES.project);
const ResourcesPage = lazy(PAGE_MODULES.resources);
const AdminPage = lazy(PAGE_MODULES.admin);
const SettingsPage = lazy(PAGE_MODULES.settings);
const NotFoundPage = lazy(PAGE_MODULES.notFound);
const GanttPage = lazy(PAGE_MODULES.gantt);
const TeamPage = lazy(PAGE_MODULES.team);
const UsagePage = lazy(PAGE_MODULES.usage);

/**
 * Loads the other pages' code once the browser is idle, so they open instantly and keep
 * working when the connection drops in the field (the service worker caches /assets/).
 */
function usePrefetchPages(): void {
  useEffect(() => {
    const run = () => {
      for (const load of Object.values(PAGE_MODULES)) void load().catch(() => undefined);
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(run, 2000);
    return () => window.clearTimeout(id);
  }, []);
}

function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const me = useMe();
  if (hasRole(me, role)) return <>{children}</>;
  return (
    <EmptyState icon={<ShieldAlert />} title="Geen toegang">
      Deze pagina is alleen beschikbaar voor {role === 'admin' ? 'beheerders' : 'planners'}.
    </EmptyState>
  );
}

export function AppRoutes() {
  const { pathname } = useLocation();
  usePrefetchPages();
  return (
    // An error on one page is forgotten when the user navigates away. The boundaries stay
    // mounted, so (router updates being transitions) the old page stays visible while a lazy page loads.
    <ErrorBoundary resetKey={pathname}>
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/mijn" element={<MyPlanningPage />} />
          <Route path="/projecten" element={<ProjectsPage />} />
          <Route path="/projecten/:uid" element={<ProjectDetailPage />} />
          <Route path="/gantt" element={<ViewFrame title="Gantt"><GanttPage /></ViewFrame>} />
          <Route path="/team" element={<ViewFrame title="Team"><TeamPage /></ViewFrame>} />
          <Route path="/bezetting" element={<ViewFrame title="Bezetting"><UsagePage /></ViewFrame>} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/:uid" element={<ResourcesPage />} />
          <Route path="/beheer" element={<Navigate to="/beheer/import" replace />} />
          <Route
            path="/beheer/:tab"
            element={
              <RequireRole role="admin">
                <AdminPage />
              </RequireRole>
            }
          />
          <Route path="/instellingen" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
